/**
 * NavigatorX Fleet Management Service
 *
 * Service layer for superadmin operations on nav_companies.
 * Reads company accounts, members, jobs, messages, and alerts
 * from the shared Firestore database.
 *
 * Firestore Schema:
 * - nav_companies/{companyId}                      — Company info
 * - nav_companies/{companyId}/members/{uid}         — Company members
 * - nav_companies/{companyId}/jobs/{jobId}           — Dispatched jobs
 * - nav_companies/{companyId}/messages/{messageId}   — Company messages
 * - nav_companies/{companyId}/alerts/{alertId}       — Fleet alerts
 *
 * @version 2.0.0 — Added company creation, deletion, missing fields, CSV export
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  QueryConstraint,
  writeBatch
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { navDb } from '../firebase/client';

// ============================================================================
// CONSTANTS
// ============================================================================

export const NAV_COMPANIES_COLLECTION = 'nav_companies';
export const NAV_USERS_COLLECTION = 'nav_users';

const INVITE_CODE_LENGTH = 8;
const INVITE_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No I/O/0/1 to avoid confusion

// ============================================================================
// TYPES
// ============================================================================

export interface FleetCompany {
  id: string;
  name: string;
  ownerUid: string;
  ownerEmail: string;
  inviteCode: string;
  seatCount: number;
  billingStatus: string;
  idleAlertMinutes: number;
  speedLimitMph: number;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  pricePerSeat: number;
  currentPeriodEnd: Timestamp | null;
  homeState: string;
  activeStates: string[];
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  // Billing contact fields
  billingEmail: string;
  billingContactName: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  tierName: string;
  customDiscount: number;
  lastInvoiceAt: Timestamp | null;
}

export interface FleetMember {
  uid: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'driver';
  status: 'active' | 'suspended' | 'removed';
  joinedAt: Timestamp | null;
  lastLocationLat?: number;
  lastLocationLng?: number;
  lastLocationHeading?: number;
  lastLocationSpeed?: number;
  isMoving: boolean;
  lastLocationAt?: Timestamp | null;
}

export interface FleetJob {
  id: string;
  title: string;
  description?: string;
  assignedTo: string;
  assignedBy: string;
  destinationLat: number;
  destinationLng: number;
  destinationName: string;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: Timestamp | null;
  acceptedAt?: Timestamp | null;
  completedAt?: Timestamp | null;
  notes?: string;
}

export interface FleetAlert {
  id: string;
  type: 'idle' | 'speeding';
  driverUid: string;
  driverName: string;
  lat: number;
  lng: number;
  createdAt: Timestamp | null;
  dismissed: boolean;
  minutesIdle?: number;
  speedMph?: number;
  speedLimitMph?: number;
}

export interface FleetMessage {
  id: string;
  fromUid: string;
  fromName: string;
  toUid: string | null;
  text: string;
  type: 'text' | 'job_update' | 'alert';
  createdAt: Timestamp | null;
}

export interface FleetInvoice {
  id: string;
  invoiceNumber: string;
  companyId: string;
  companyName: string;
  billingEmail: string;
  periodStart: string;
  periodEnd: string;
  seatCount: number;
  pricePerSeat: number;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'void';
  paidAt: Timestamp | null;
  paidAmount: number;
  pdfDownloadUrl: string;
  dueDate: Timestamp | null;
  sentAt: Timestamp | null;
  remindersSent: number;
  notes: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface FleetQuote {
  id: string;
  quoteNumber: string;
  companyId: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  seatCount: number;
  pricePerSeat: number;
  tierName: string;
  discountPercent: number;
  monthlyTotal: number;
  annualEstimate: number;
  validUntil: Timestamp | null;
  status: 'draft' | 'sent' | 'accepted' | 'expired' | 'declined';
  pdfDownloadUrl: string;
  sentAt: Timestamp | null;
  notes: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface FleetStats {
  totalCompanies: number;
  totalMembers: number;
  activeCompanies: number;
  trialingCompanies: number;
  totalSeats: number;
  seatUtilizationPercent: number;
  estimatedMrr: number;
  totalMrr: number;
  overdueInvoiceCount: number;
}

export interface CreateCompanyInput {
  name: string;
  ownerEmail: string;
  seatCount: number;
  billingStatus: string;
  homeState: string;
  activeStates: string[];
  idleAlertMinutes: number;
  speedLimitMph: number;
}

// ============================================================================
// HELPERS
// ============================================================================

function parseCompanyDoc(docSnap: { id: string; data: () => any }): FleetCompany {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name ?? '',
    ownerUid: data.ownerUid ?? '',
    ownerEmail: data.ownerEmail ?? '',
    inviteCode: data.inviteCode ?? '',
    seatCount: data.seatCount ?? 0,
    billingStatus: data.billingStatus ?? 'UNKNOWN',
    idleAlertMinutes: data.idleAlertMinutes ?? 30,
    speedLimitMph: data.speedLimitMph ?? 75,
    stripeCustomerId: data.stripeCustomerId ?? '',
    stripeSubscriptionId: data.stripeSubscriptionId ?? '',
    pricePerSeat: data.pricePerSeat ?? 0,
    currentPeriodEnd: data.currentPeriodEnd ?? null,
    homeState: data.homeState ?? '',
    activeStates: data.activeStates ?? [],
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    billingEmail: data.billingEmail ?? '',
    billingContactName: data.billingContactName ?? '',
    billingAddress: data.billingAddress ?? '',
    billingCity: data.billingCity ?? '',
    billingState: data.billingState ?? '',
    billingZip: data.billingZip ?? '',
    tierName: data.tierName ?? '',
    customDiscount: data.customDiscount ?? 0,
    lastInvoiceAt: data.lastInvoiceAt ?? null,
  };
}

/**
 * Generate a random invite code and verify uniqueness across all companies.
 */
async function generateUniqueInviteCode(): Promise<string> {
  const maxAttempts = 10;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let code = '';
    for (let i = 0; i < INVITE_CODE_LENGTH; i++) {
      code += INVITE_CODE_CHARS.charAt(Math.floor(Math.random() * INVITE_CODE_CHARS.length));
    }

    // Check uniqueness
    const existing = await getDocs(
      query(collection(navDb, NAV_COMPANIES_COLLECTION), where('inviteCode', '==', code), limit(1))
    );
    if (existing.empty) return code;
  }
  throw new Error('Failed to generate a unique invite code after multiple attempts');
}

/**
 * Look up a nav_user by email and return their UID + display name.
 */
async function lookupNavUserByEmail(email: string): Promise<{ uid: string; displayName: string } | null> {
  const snapshot = await getDocs(
    query(collection(navDb, NAV_USERS_COLLECTION), where('email', '==', email), limit(1))
  );
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return {
    uid: docSnap.id,
    displayName: docSnap.data().displayName ?? docSnap.data().name ?? '',
  };
}

// ============================================================================
// COMPANY OPERATIONS
// ============================================================================

/**
 * Fetch all fleet companies.
 */
export async function getFleetCompanies(): Promise<FleetCompany[]> {
  const snapshot = await getDocs(
    query(collection(navDb, NAV_COMPANIES_COLLECTION), orderBy('createdAt', 'desc'))
  );
  return snapshot.docs.map(parseCompanyDoc);
}

/**
 * Fetch a single company by ID.
 */
export async function getFleetCompany(companyId: string): Promise<FleetCompany | null> {
  const docSnap = await getDoc(doc(navDb, NAV_COMPANIES_COLLECTION, companyId));
  if (!docSnap.exists()) return null;
  return parseCompanyDoc(docSnap);
}

/**
 * Create a new fleet company.
 *
 * 1. Validates the owner email exists in nav_users
 * 2. Generates a unique invite code
 * 3. Creates the company document
 * 4. Adds the owner as the first member
 * 5. Updates the owner's nav_users document with company flags
 *
 * Returns the created company ID and invite code.
 */
export async function createFleetCompany(input: CreateCompanyInput): Promise<{ companyId: string; inviteCode: string }> {
  // Validate owner exists
  const owner = await lookupNavUserByEmail(input.ownerEmail);
  if (!owner) {
    throw new Error(`No NavigatorX user found with email "${input.ownerEmail}". The owner must have a NavigatorX account first.`);
  }

  // Check owner isn't already in a company
  const existingCompany = await getDocs(
    query(collection(navDb, NAV_COMPANIES_COLLECTION), where('ownerUid', '==', owner.uid), limit(1))
  );
  if (!existingCompany.empty) {
    throw new Error(`User "${input.ownerEmail}" already owns a company: "${existingCompany.docs[0].data().name}"`);
  }

  // Generate unique invite code
  const inviteCode = await generateUniqueInviteCode();

  const now = Timestamp.now();

  // Create company document
  const companyRef = await addDoc(collection(navDb, NAV_COMPANIES_COLLECTION), {
    name: input.name,
    ownerUid: owner.uid,
    ownerEmail: input.ownerEmail,
    inviteCode,
    seatCount: input.seatCount,
    billingStatus: input.billingStatus,
    idleAlertMinutes: input.idleAlertMinutes,
    speedLimitMph: input.speedLimitMph,
    homeState: input.homeState,
    activeStates: input.activeStates,
    stripeCustomerId: '',
    stripeSubscriptionId: '',
    pricePerSeat: 0,
    currentPeriodEnd: null,
    createdAt: now,
    updatedAt: now,
  });

  // Add owner as first member
  await setDoc(doc(navDb, NAV_COMPANIES_COLLECTION, companyRef.id, 'members', owner.uid), {
    email: input.ownerEmail,
    displayName: owner.displayName,
    role: 'owner',
    status: 'active',
    joinedAt: now,
    isMoving: false,
  });

  // Update owner's nav_users document
  await updateDoc(doc(navDb, NAV_USERS_COLLECTION, owner.uid), {
    isCompanyUser: true,
    companyAccountId: companyRef.id,
    companyRole: 'owner',
    companyName: input.name,
    locationSharingEnabled: true,
  });

  return { companyId: companyRef.id, inviteCode };
}

/**
 * Update a company document (superadmin override).
 */
export async function updateFleetCompany(
  companyId: string,
  updates: Partial<Pick<FleetCompany, 'name' | 'seatCount' | 'billingStatus' | 'idleAlertMinutes' | 'speedLimitMph' | 'homeState' | 'activeStates'>>
): Promise<void> {
  await updateDoc(doc(navDb, NAV_COMPANIES_COLLECTION, companyId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a fleet company and clean up all member references.
 *
 * 1. Sets all members to "removed" status
 * 2. Clears company flags from each member's nav_users document
 * 3. Deletes the company document
 */
export async function deleteFleetCompany(companyId: string): Promise<void> {
  // Get all members
  const membersSnap = await getDocs(
    collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'members')
  );

  // Clear company flags from each member's nav_users document
  const batch = writeBatch(navDb);
  for (const memberDoc of membersSnap.docs) {
    const navUserRef = doc(navDb, NAV_USERS_COLLECTION, memberDoc.id);
    batch.update(navUserRef, {
      isCompanyUser: false,
      companyAccountId: null,
      companyRole: null,
      companyName: null,
      locationSharingEnabled: false,
    });
    // Mark member as removed
    batch.update(memberDoc.ref, { status: 'removed' });
  }
  await batch.commit();

  // Delete the company document
  await deleteDoc(doc(navDb, NAV_COMPANIES_COLLECTION, companyId));
}

// ============================================================================
// MEMBER OPERATIONS
// ============================================================================

/**
 * Fetch all members for a company.
 */
export async function getFleetMembers(companyId: string): Promise<FleetMember[]> {
  const snapshot = await getDocs(
    collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'members')
  );

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      uid: docSnap.id,
      email: data.email ?? '',
      displayName: data.displayName ?? '',
      role: data.role ?? 'driver',
      status: data.status ?? 'active',
      joinedAt: data.joinedAt ?? null,
      lastLocationLat: data.lastLocationLat,
      lastLocationLng: data.lastLocationLng,
      lastLocationHeading: data.lastLocationHeading,
      lastLocationSpeed: data.lastLocationSpeed,
      isMoving: data.isMoving ?? false,
      lastLocationAt: data.lastLocationAt ?? null,
    };
  });
}

/**
 * Update a member's role or status.
 */
export async function updateFleetMember(
  companyId: string,
  uid: string,
  updates: Partial<Pick<FleetMember, 'role' | 'status'>>
): Promise<void> {
  await updateDoc(
    doc(navDb, NAV_COMPANIES_COLLECTION, companyId, 'members', uid),
    updates
  );
}

/**
 * Remove a member from a company and clear their nav_users company flags.
 */
export async function removeFleetMember(companyId: string, uid: string): Promise<void> {
  const batch = writeBatch(navDb);

  // Mark as removed in company
  batch.update(
    doc(navDb, NAV_COMPANIES_COLLECTION, companyId, 'members', uid),
    { status: 'removed' }
  );

  // Clear company flags from nav_users
  batch.update(doc(navDb, NAV_USERS_COLLECTION, uid), {
    isCompanyUser: false,
    companyAccountId: null,
    companyRole: null,
    companyName: null,
    locationSharingEnabled: false,
  });

  await batch.commit();
}

// ============================================================================
// JOB OPERATIONS
// ============================================================================

/**
 * Fetch jobs for a company.
 */
export async function getFleetJobs(companyId: string, statusFilter?: string): Promise<FleetJob[]> {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc'), limit(100)];
  if (statusFilter) {
    constraints.unshift(where('status', '==', statusFilter));
  }

  const snapshot = await getDocs(
    query(collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'jobs'), ...constraints)
  );

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      title: data.title ?? '',
      description: data.description,
      assignedTo: data.assignedTo ?? '',
      assignedBy: data.assignedBy ?? '',
      destinationLat: data.destinationLat ?? 0,
      destinationLng: data.destinationLng ?? 0,
      destinationName: data.destinationName ?? '',
      status: data.status ?? 'pending',
      createdAt: data.createdAt ?? null,
      acceptedAt: data.acceptedAt ?? null,
      completedAt: data.completedAt ?? null,
      notes: data.notes,
    };
  });
}

// ============================================================================
// ALERT OPERATIONS
// ============================================================================

/**
 * Fetch alerts for a company.
 */
export async function getFleetAlerts(companyId: string, dismissedFilter?: boolean): Promise<FleetAlert[]> {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc'), limit(100)];
  if (dismissedFilter !== undefined) {
    constraints.unshift(where('dismissed', '==', dismissedFilter));
  }

  const snapshot = await getDocs(
    query(collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'alerts'), ...constraints)
  );

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      type: data.type ?? 'idle',
      driverUid: data.driverUid ?? '',
      driverName: data.driverName ?? '',
      lat: data.lat ?? 0,
      lng: data.lng ?? 0,
      createdAt: data.createdAt ?? null,
      dismissed: data.dismissed ?? false,
      minutesIdle: data.minutesIdle,
      speedMph: data.speedMph,
      speedLimitMph: data.speedLimitMph,
    };
  });
}

// ============================================================================
// MESSAGE OPERATIONS
// ============================================================================

/**
 * Fetch messages for a company.
 */
export async function getFleetMessages(companyId: string): Promise<FleetMessage[]> {
  const snapshot = await getDocs(
    query(
      collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(100)
    )
  );

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      fromUid: data.fromUid ?? '',
      fromName: data.fromName ?? '',
      toUid: data.toUid ?? null,
      text: data.text ?? '',
      type: data.type ?? 'text',
      createdAt: data.createdAt ?? null,
    };
  });
}

// ============================================================================
// AGGREGATE STATS
// ============================================================================

/**
 * Load aggregate fleet stats across all companies.
 */
export async function getFleetStats(): Promise<FleetStats> {
  const companies = await getFleetCompanies();

  let totalMembers = 0;
  let totalSeats = 0;
  let activeCompanies = 0;
  let trialingCompanies = 0;
  let estimatedMrr = 0;

  for (const company of companies) {
    totalSeats += company.seatCount;
    if (company.billingStatus === 'ACTIVE') {
      activeCompanies++;
      estimatedMrr += company.seatCount * company.pricePerSeat;
    }
    if (company.billingStatus === 'TRIALING') trialingCompanies++;
  }

  // Count members across all companies
  const memberCounts = await Promise.allSettled(
    companies.map(async (company) => {
      const membersSnap = await getDocs(
        query(
          collection(navDb, NAV_COMPANIES_COLLECTION, company.id, 'members'),
          where('status', '==', 'active')
        )
      );
      return membersSnap.size;
    })
  );

  for (const result of memberCounts) {
    if (result.status === 'fulfilled') {
      totalMembers += result.value;
    }
  }

  const seatUtilizationPercent = totalSeats > 0 ? Math.round((totalMembers / totalSeats) * 100) : 0;

  // Count overdue invoices across all companies
  let overdueInvoiceCount = 0;
  const overdueCountResults = await Promise.allSettled(
    companies.map(async (company) => {
      const overdueSnap = await getDocs(
        query(
          collection(navDb, NAV_COMPANIES_COLLECTION, company.id, 'invoices'),
          where('status', '==', 'overdue')
        )
      );
      return overdueSnap.size;
    })
  );
  for (const result of overdueCountResults) {
    if (result.status === 'fulfilled') {
      overdueInvoiceCount += result.value;
    }
  }

  return {
    totalCompanies: companies.length,
    totalMembers,
    activeCompanies,
    trialingCompanies,
    totalSeats,
    seatUtilizationPercent,
    estimatedMrr,
    totalMrr: estimatedMrr,
    overdueInvoiceCount,
  };
}

// ============================================================================
// CSV EXPORT
// ============================================================================

/**
 * Generate a CSV string of all fleet companies with member counts.
 */
export async function exportFleetCompaniesCSV(
  companies: FleetCompany[],
  memberCounts: Record<string, number>
): Promise<string> {
  const headers = [
    'Company Name',
    'Owner Email',
    'Billing Status',
    'Members',
    'Seat Count',
    'Invite Code',
    'Home State',
    'Active States',
    'Price Per Seat',
    'Idle Alert (min)',
    'Speed Limit (mph)',
    'Stripe Customer ID',
    'Created',
    'Company ID',
  ];

  const rows = companies.map(company => {
    const createdDate = company.createdAt
      ? (company.createdAt.toDate ? company.createdAt.toDate() : new Date(company.createdAt as any))
      : null;

    return [
      escapeCsvField(company.name),
      escapeCsvField(company.ownerEmail),
      company.billingStatus,
      String(memberCounts[company.id] ?? 0),
      String(company.seatCount),
      company.inviteCode,
      company.homeState,
      escapeCsvField(company.activeStates.join('; ')),
      String(company.pricePerSeat),
      String(company.idleAlertMinutes),
      String(company.speedLimitMph),
      company.stripeCustomerId,
      createdDate ? createdDate.toISOString().split('T')[0] : '',
      company.id,
    ].join(',');
  });

  return [headers.join(','), ...rows].join('\n');
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// ============================================================================
// REAL-TIME SUBSCRIPTIONS
// ============================================================================

/**
 * Subscribe to real-time updates on the companies collection.
 */
export function subscribeToFleetCompanies(
  callback: (companies: FleetCompany[]) => void
): Unsubscribe {
  return onSnapshot(
    query(collection(navDb, NAV_COMPANIES_COLLECTION), orderBy('createdAt', 'desc')),
    (snapshot) => {
      callback(snapshot.docs.map(parseCompanyDoc));
    }
  );
}

// ============================================================================
// INVOICE OPERATIONS
// ============================================================================

/**
 * Fetch all invoices for a company.
 */
export async function getFleetInvoices(companyId: string): Promise<FleetInvoice[]> {
  const snapshot = await getDocs(
    query(
      collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'invoices'),
      orderBy('createdAt', 'desc'),
      limit(100)
    )
  );

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      invoiceNumber: data.invoiceNumber ?? '',
      companyId: data.companyId ?? '',
      companyName: data.companyName ?? '',
      billingEmail: data.billingEmail ?? '',
      periodStart: data.periodStart ?? '',
      periodEnd: data.periodEnd ?? '',
      seatCount: data.seatCount ?? 0,
      pricePerSeat: data.pricePerSeat ?? 0,
      subtotal: data.subtotal ?? 0,
      discountPercent: data.discountPercent ?? 0,
      discountAmount: data.discountAmount ?? 0,
      total: data.total ?? 0,
      status: data.status ?? 'draft',
      paidAt: data.paidAt ?? null,
      paidAmount: data.paidAmount ?? 0,
      pdfDownloadUrl: data.pdfDownloadUrl ?? '',
      dueDate: data.dueDate ?? null,
      sentAt: data.sentAt ?? null,
      remindersSent: data.remindersSent ?? 0,
      notes: data.notes ?? '',
      createdAt: data.createdAt ?? null,
      updatedAt: data.updatedAt ?? null,
    };
  });
}

// ============================================================================
// QUOTE OPERATIONS
// ============================================================================

/**
 * Fetch all quotes for a company.
 */
export async function getFleetQuotes(companyId: string): Promise<FleetQuote[]> {
  const snapshot = await getDocs(
    query(
      collection(navDb, NAV_COMPANIES_COLLECTION, companyId, 'quotes'),
      orderBy('createdAt', 'desc'),
      limit(100)
    )
  );

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      quoteNumber: data.quoteNumber ?? '',
      companyId: data.companyId ?? '',
      companyName: data.companyName ?? '',
      contactName: data.contactName ?? '',
      contactEmail: data.contactEmail ?? '',
      seatCount: data.seatCount ?? 0,
      pricePerSeat: data.pricePerSeat ?? 0,
      tierName: data.tierName ?? '',
      discountPercent: data.discountPercent ?? 0,
      monthlyTotal: data.monthlyTotal ?? 0,
      annualEstimate: data.annualEstimate ?? 0,
      validUntil: data.validUntil ?? null,
      status: data.status ?? 'draft',
      pdfDownloadUrl: data.pdfDownloadUrl ?? '',
      sentAt: data.sentAt ?? null,
      notes: data.notes ?? '',
      createdAt: data.createdAt ?? null,
      updatedAt: data.updatedAt ?? null,
    };
  });
}

// ============================================================================
// BILLING CONTACT UPDATES
// ============================================================================

/**
 * Update billing contact fields on a company.
 */
export async function updateFleetCompanyBilling(
  companyId: string,
  billing: {
    billingEmail?: string;
    billingContactName?: string;
    billingAddress?: string;
    billingCity?: string;
    billingState?: string;
    billingZip?: string;
  }
): Promise<void> {
  await updateDoc(doc(navDb, NAV_COMPANIES_COLLECTION, companyId), {
    ...billing,
    updatedAt: Timestamp.now(),
  });
}

// ============================================================================
// CALLABLE FUNCTION WRAPPERS
// ============================================================================

const functions = getFunctions();

export async function callCreateFleetSubscription(companyId: string): Promise<{
  subscriptionId: string;
  customerId: string;
  seatCount: number;
  pricePerSeat: number;
}> {
  const fn = httpsCallable<{ companyId: string }, {
    subscriptionId: string;
    customerId: string;
    seatCount: number;
    pricePerSeat: number;
  }>(functions, 'createFleetSubscription');
  const result = await fn({ companyId });
  return result.data;
}

export async function callUpdateFleetSubscription(
  companyId: string,
  updates: { newSeatCount?: number; newPricePerSeat?: number }
): Promise<void> {
  const fn = httpsCallable(functions, 'updateFleetSubscription');
  await fn({ companyId, ...updates });
}

export async function callCancelFleetSubscription(
  companyId: string,
  immediate: boolean
): Promise<{ billingStatus: string }> {
  const fn = httpsCallable<{ companyId: string; immediate: boolean }, { success: boolean; billingStatus: string }>(
    functions, 'cancelFleetSubscription'
  );
  const result = await fn({ companyId, immediate });
  return result.data;
}

export async function callGenerateFleetQuote(params: {
  companyId: string;
  seatCount: number;
  pricePerSeat?: number;
  contactName: string;
  contactEmail: string;
  notes?: string;
  sendEmail?: boolean;
}): Promise<{ quoteId: string; quoteNumber: string; pdfDownloadUrl: string }> {
  const fn = httpsCallable<typeof params, { quoteId: string; quoteNumber: string; pdfDownloadUrl: string }>(
    functions, 'generateFleetQuote'
  );
  const result = await fn(params);
  return result.data;
}

export async function callGenerateFleetInvoice(params: {
  companyId: string;
  periodStart?: string;
  periodEnd?: string;
  notes?: string;
  sendEmail?: boolean;
}): Promise<{ invoiceId: string; invoiceNumber: string; pdfDownloadUrl: string }> {
  const fn = httpsCallable<typeof params, { invoiceId: string; invoiceNumber: string; pdfDownloadUrl: string }>(
    functions, 'generateFleetInvoice'
  );
  const result = await fn(params);
  return result.data;
}

export async function callSendFleetInvoice(
  companyId: string,
  invoiceId: string
): Promise<void> {
  const fn = httpsCallable(functions, 'sendFleetInvoice');
  await fn({ companyId, invoiceId });
}

export async function callSetCustomPricing(params: {
  companyId: string;
  pricePerSeat?: number;
  customDiscount?: number;
  tierName?: string;
}): Promise<void> {
  const fn = httpsCallable(functions, 'setCustomPricing');
  await fn(params);
}

export async function callGetBillingPortalUrl(
  companyId: string
): Promise<string> {
  const fn = httpsCallable<{ companyId: string }, { url: string }>(
    functions, 'getFleetBillingPortalUrl'
  );
  const result = await fn({ companyId });
  return result.data.url;
}

// ============================================================================
// US STATES
// ============================================================================

export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
] as const;
